import React from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DealStagePicker from "@/components/deals/DealStagePicker";
import type Contact from "@/model/interface/Contact";
import type DealStage from "@/model/interface/DealStage";
import { ContactType } from "@/enum/ContactType";
import { useConvertContactMutation, useGetDealStagesQuery, usePromoteContactMutation } from "@/store/rest_api";
import { useMoveDealStage } from "@/hooks/useMoveDealStage";
import { useConversionToast } from "@/hooks/useConversionToast";
import { getContactDisplayName } from "@/util/string";
import { apiErrorMessage } from "@/util/apiError";

interface LeadStagePanelProps {
  contact: Contact;
}

const LeadStagePanel: React.FC<LeadStagePanelProps> = ({ contact }) => {
  const { data: stages = [] as DealStage[] } = useGetDealStagesQuery({});
  const { moveDealStage, isMoving } = useMoveDealStage();
  const [convertContact, { isLoading: isConverting }] = useConvertContactMutation();
  const [promoteContact, { isLoading: isPromoting }] = usePromoteContactMutation();
  const showConversionToast = useConversionToast();

  const personIsLead = contact.type === ContactType.LEAD;
  const activeDeal = contact.activeDeal;
  const personName = getContactDisplayName(contact);

  const handleConvert = async () => {
    try {
      const deal = await convertContact(contact.id).unwrap();
      showConversionToast(deal.convertedContactId, personName);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not convert the lead"));
    }
  };

  const handlePromote = async () => {
    try {
      const deal = await promoteContact(contact.id).unwrap();
      if (deal.convertedContactId) {
        showConversionToast(deal.convertedContactId, personName);
      } else {
        toast.success(`Added to ${deal.stage?.name ?? "the pipeline"}`);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not promote the lead"));
    }
  };

  return (
    <div className="mt-3 space-y-3 rounded-[var(--radius)] border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stage</span>
        {personIsLead && !contact.deletedAt && (
          <div className="flex items-center gap-2">
            {!activeDeal && (
              <Button variant="outline" size="sm" onClick={() => void handlePromote()} disabled={isPromoting}>
                Promote
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => void handleConvert()} disabled={isConverting}>
              Convert to contact
            </Button>
          </div>
        )}
      </div>
      {activeDeal ? (
        <DealStagePicker
          stages={stages}
          currentStageId={activeDeal.stage?.id}
          personIsLead={personIsLead}
          disabled={isMoving || !!contact.deletedAt}
          onPick={(stage) => void moveDealStage({ id: activeDeal.id, contact }, stage.id)}
        />
      ) : (
        <p className="text-sm text-muted-foreground">No deal yet. Promote starts one in the default stage.</p>
      )}
    </div>
  );
};

export default LeadStagePanel;
